export const RELEASE_NOTES_URL = "/manual?page=release-notes";

// Desktop version strings carry decoration on both ends (test_release_ prefixes,
// -mfe / -mfe-rc.N suffixes). The changelog keys releases on bare semver and
// discards anything else, so reduce to that before handing it over.
const SEMVER = /(\d+\.\d+\.\d+)/;

/** The changelog URL, focused on a release when the version is recognisable. */
export function releaseNotesUrlFor(version: string): string {
  const match = version.match(SEMVER);
  return match ? `${RELEASE_NOTES_URL}&version=${match[1]}` : RELEASE_NOTES_URL;
}

type ElectronReleaseNotesApi = {
  consumeReleaseNotesPrompt?: () => Promise<string | null>;
};

function electronApi(): ElectronReleaseNotesApi | undefined {
  return (window as unknown as { electronAPI?: ElectronReleaseNotesApi })
    .electronAPI;
}

/**
 * Open the manual at this release's notes, once, on the first launch after the
 * desktop app updated.
 *
 * The desktop main process owns the decision — it holds both the installed
 * version and the persisted last-seen one. We pull rather than listen for a
 * push so there is no race against renderer load or single-spa mounting the
 * manual MFE. Resolves null in a browser, on a fresh install, and on every
 * launch after the first one on a given version.
 */
export async function promptReleaseNotesIfUpdated(): Promise<boolean> {
  const api = electronApi();
  if (!api?.consumeReleaseNotesPrompt) return false;

  let version: string | null = null;
  try {
    version = await api.consumeReleaseNotesPrompt();
  } catch {
    // A broken bridge must never block the app from starting.
    return false;
  }
  if (!version) return false;

  // MFE routes are entered by pushing the URL and re-emitting popstate, the
  // same way SidebarItem navigates — the tanstack router owns only
  // shell-owned paths.
  window.history.pushState({}, "", releaseNotesUrlFor(version));
  window.dispatchEvent(
    new PopStateEvent("popstate", { state: window.history.state }),
  );
  return true;
}
